import QrScanner from "qr-scanner";
import { useEffect, useRef, useState } from "react";
import "./QRScanner.scss";
import { Event } from "../../../Models/Event";
import { Button } from "flowbite-react";
import { toast } from "react-toastify";
import { User } from "../../../Models/User";
import UserApi from "../../../Api/UserApi";
import EventApi from "../../../Api/EventApi";
import { EventParticipant } from "../../../Models/EventParticipant";
import { displayDateOnly } from "../../../Utils";
import { Member } from "../../../Models/Member";
import MemberApi from "../../../Api/MemberApi";
import { UserListRequest } from "../../../Models/UserListRequest";

export default function QRScanner({
  selectedEvent,
  handleAddUserSave,
}: {
  selectedEvent: Event | null;
  handleAddUserSave: () => void;
}) {
  const videoElementRef = useRef(null);
  const [scanned, setScannedText] = useState("");
  const [userId, setUserId] = useState<number>(0);
  const [currentUser, setcurrentUser] = useState<User | null>(null);
  const [currentParticipant, setcurrentParticipant] =
    useState<EventParticipant | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);

  const [userListFormData, setUserListFormData] = useState<UserListRequest>({
    firstName: "",
    lastName: "",
    emailAddress: "",
  });

  useEffect(() => {
    MemberApi.getMembers()
      .then((res) => {
        setMembers(res.data);
      })
      .catch((err) => {
        console.error("Error loading members:", err);
      });
    const video: HTMLVideoElement = videoElementRef.current!;
    const qrScanner = new QrScanner(
      video,
      (result) => {
        const resultArray = result.data.split("/");
        if (resultArray.length === 6) {
          const linkType = resultArray[3];
          if (linkType !== "user") {
            setScannedText("Invalid Link");
            setUserId(0);
            setcurrentUser(null);
            setcurrentParticipant(null);
          } else {
            const tempUserId = resultArray[5];
            setUserId(Number(tempUserId));
            setScannedText(result.data);
          }
        } else {
          setScannedText("Invalid Link");
          setUserId(0);
          setcurrentUser(null);
          setcurrentParticipant(null);
        }
      },
      {
        returnDetailedScanResult: true,
        highlightScanRegion: true,
        highlightCodeOutline: true,
      }
    );
    qrScanner.start();

    return () => {
      qrScanner.stop();
      qrScanner.destroy();
    };
  }, []);

  useEffect(() => {
    if (selectedMemberId) {
      MemberApi.getMemberUserList(selectedMemberId, userListFormData)
        .then((res) => setUsers(res.data))
        .catch((err) => toast.error("Failed to load users"));
    } else {
      setUsers([]);
    }
  }, [selectedMemberId]);

  useEffect(() => {
    if (selectedEvent && userId > 0) {
      UserApi.getUser(userId.toString())
        .then((res) => {
          setcurrentUser(res.data);
          EventApi.getEventParticipant(selectedEvent.id, userId)
            .then((res2) => {
              setcurrentParticipant(res2.data);
            })
            .catch((err2) => {
              console.log(err2);
            });
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [userId]);

  const addUserToEvent = () => {
    if (currentUser && selectedEvent) {
      EventApi.addUserToEvent(selectedEvent.id, currentUser)
        .then((res) => {
          if (res.data.success) {
            setUserId(0);
            setcurrentUser(null);
            setcurrentParticipant(null);
            handleAddUserSave();
            toast.success("Participant registered. A confirmation email has been sent.");
          } else {
            toast.error(res.data.message);
          }
        })
        .catch((err) => {
          console.log(err);
        });
    }
  };

  const buttonDisabled = () => {
    if (currentParticipant) return true;
    if (currentUser === null || selectedEvent === null) return true;
    return false;
  };

  return (
    <div>
      <p className="scannedText">Event: {selectedEvent?.name}</p>
      <div className="my-4">
        <label className="block mb-1 font-medium">Select Member</label>
        <select
          className="p-2 border rounded w-full"
          value={selectedMemberId ?? ""}
          onChange={(e) => setSelectedMemberId(Number(e.target.value))}
        >
          <option value="">-- Select Member --</option>
          {members.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>

        {users.length > 0 && (
          <>
            <label className="block mt-3 mb-1 font-medium">Select User</label>
            <select
              className="p-2 border rounded w-full"
              onChange={(e) => {
                const selected = users.find(
                  (u) => u.id === Number(e.target.value)
                );
                if (selected) {
                  setUserId(selected.id);
                  setcurrentUser(selected);
                }
              }}
            >
              <option value="">-- Select User --</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.firstName} {u.lastName} ({u.userLevel?.name})
                </option>
              ))}
            </select>
          </>
        )}
      </div>
      -OR-
      <div className="videoWrapper">
        <video className="qrVideo" ref={videoElementRef} />
      </div>
      {scanned && <p className="scannedText">URL: {scanned}</p>}
      {currentUser && (
        <p className="scannedText">
          User: {currentUser?.firstName} {currentUser?.lastName}
        </p>
      )}
      {currentParticipant && (
        <p className="scannedText">
          Added on: {displayDateOnly(new Date(currentParticipant.dateCreated!))}
        </p>
      )}
      <div className="float-right">
        <Button
          type="button"
          className="buttonMain"
          onClick={() => addUserToEvent()}
          disabled={buttonDisabled()}
        >
          Add User
        </Button>
      </div>
    </div>
  );
}
